import {Box, Text} from 'exsportia-components'
import {useFormikContext} from 'formik'
import React, {FC, memo, useCallback, useEffect, useMemo, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'

import {makeGetInstructorServicesEndpoint} from '../../api/booking/get-instructor-services'
import {makeGetVenueServicesEndpoint} from '../../api/booking/get-venue-services'
import {makeGetVenueClassesEndpoint} from '../../api/class/get-venue-classes'
import {GridLayout} from '../../layout/grid-layout/grid-layout'
import {SimpleGridLayoutHeader} from '../../layout/grid-layout/headers/simple-grid-layout-header'
import {TranslatorScope} from '../../locale/types'
import {API} from '../../networking/http'
import {IntegratedSignUpForm, IntegratedSignUpFormType} from '../../services/auth/forms/integrated-sign-up-form'
import {useAuthState} from '../../services/auth/hooks'
import {useLocale} from '../../utils/i18n'
import {useModalActions} from '../../utils/modals'
import {
  FormStepValues,
  getFormEntityIds,
  getFormEntityType,
  getStepAfterSummary,
  hasFormForEntities,
} from '../booking-form/form-step'
import {validationTokensMap} from '../select-service/select-service-page'
import {ClassSummarySection} from './class-summary-section'
import {EntityWithPermission, findRestrictedPermission, getEntityIdsToValidate} from './entity-permission'
import {ServiceSummarySection} from './service-summary-section'
import {BookingCommonType, UserInfoSection} from './user-info-section'

const Header: FC<{onBack?: () => void}> = memo(({onBack}) => {
  const {l} = useLocale()
  return (
    <SimpleGridLayoutHeader
      title={l('customer')}
      customBackAction={onBack}
    />
  )
})

type Props = {
  type: 'class' | 'service'
}

export const BookingSummary: FC<Props> = ({type}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [wholePrice, setWholePrice] = useState<number | undefined>()
  const {isLoggedIn} = useAuthState()
  const navigate = useNavigate()
  const {venueId} = useParams()
  const {openModal, closeAllModals} = useModalActions()
  const {l} = useLocale()
  const {submitForm, setValues, values: bookingState} = useFormikContext<BookingCommonType>()
  const {service, class: cls} = bookingState
  const {paymentOnExsportia = false} = service || cls || {}
  const {splitPayment = false} = service || {}
  const withPaymentStep = useMemo(
    () => paymentOnExsportia && (splitPayment || wholePrice !== 0),
    [paymentOnExsportia, wholePrice],
  )

  const handleBack = useCallback(() => {
    const sessions = (bookingState as any).selectedSessions // eslint-disable-line
    const isMulti = type === 'class' && sessions?.length > 1
    if (!isMulti) {
      navigate(-1)
      return
    }
    const sharedConf = (bookingState as any).sharedParticipantsConfig !== false // eslint-disable-line
    if (sharedConf) {
      // Shared participants were entered once → pop back to that single participants step.
      navigate(-1)
      return
    }
    // Per-session mode: re-open the LAST session's participants step (not the single-session
    // flow), restoring that session's participants. set-participants sits directly below
    // summary in history, so we restore state then pop rather than push a new entry. WBA-4046.
    const lastIndex = sessions.length - 1
    const lastSession = sessions[lastIndex]
    setValues({
      ...bookingState,
      currentSessionIndex: lastIndex,
      participants: (bookingState as any).perSessionParticipants?.[lastSession.sessionId] || [], // eslint-disable-line
      sessionId: lastSession.sessionId,
      timestamp: lastSession.timestamp,
      classId: lastSession.classId,
      areaId: lastSession.areaId,
      maxParticipants: lastSession.maxParticipants,
      duration: lastSession.duration,
      date: lastSession.date,
      instructors: lastSession.instructors,
      useForParticipants: (bookingState as any).perSessionUseForParticipants?.[lastSession.sessionId] ?? false, // eslint-disable-line
      sportType: lastSession.sportType,
    } as any) // eslint-disable-line
    navigate(-1)
  }, [type, bookingState, setValues, navigate])

  const selectedSessions = 'selectedSessions' in bookingState ? bookingState.selectedSessions : undefined
  const entityIdsKey = getEntityIdsToValidate(type, {class: cls, service, selectedSessions}).join(',')
  const bookingType = 'bookingType' in bookingState ? bookingState.bookingType : undefined
  const instructorId = 'teacherId' in bookingState ? bookingState.teacherId : undefined

  const validateEntityPermission = useCallback(async (): Promise<boolean> => {
    const entityIds = entityIdsKey ? entityIdsKey.split(',') : []
    if (entityIds.length === 0 || !venueId) {
      return true
    }

    try {
      let endpoint: string
      let params: Record<string, unknown>

      if (type === 'class') {
        endpoint = makeGetVenueClassesEndpoint()
        params = {venueId}
      } else if (bookingType === 'teacher' && instructorId) {
        endpoint = makeGetInstructorServicesEndpoint()
        params = {venueId, instructorId}
      } else {
        endpoint = makeGetVenueServicesEndpoint()
        params = {venueId, isJustTheSpace: bookingType === 'space'}
      }

      const freshData = await API.get(endpoint, {params}).then((res) => res.data)

      const permissionData = findRestrictedPermission(freshData as EntityWithPermission[], entityIds)

      if (permissionData) {
        const reasonKey =
          permissionData.reason && permissionData.reason in validationTokensMap
            ? validationTokensMap[permissionData.reason as keyof typeof validationTokensMap]
            : 'entity_is_not_available'

        openModal({
          title: l('access_restricted'),
          component: <Text>{l(reasonKey as TranslatorScope, {caseAction: 'capitalize'})}</Text>,
          footerConfig: {
            submitButton: {
              text: l('back_to_venue'),
              settings: {width: 'max-content'},
              onClick: () => {
                closeAllModals()
                navigate(`/${venueId}/`)
              },
            },
          },
        })
        return false
      }
      return true
    } catch (err) {
      return true
    }
  }, [type, venueId, entityIdsKey, bookingType, instructorId, openModal, closeAllModals, navigate, l])

  const handleBook = useCallback(
    (values: IntegratedSignUpFormType) => {
      setValues({...bookingState, ...values, paymentType: 'payOnLocation'})
      if (values.isRequestedToCreateAccount && !isLoggedIn) {
        return
      }
      setIsLoading(true)
      validateEntityPermission().then(async (hasPermission) => {
        if (!hasPermission) {
          setIsLoading(false)
          return
        }
        // Look the form up while the Book button spins, so a booking without a form never passes
        // through an empty Form step.
        const formStepValues = bookingState as FormStepValues
        const hasForm = await hasFormForEntities(
          getFormEntityType(type, formStepValues),
          getFormEntityIds(type, formStepValues),
        )
        const nextStep = getStepAfterSummary(hasForm, withPaymentStep)
        if (nextStep.kind === 'submit') {
          submitForm().catch(console.error)
        } else if (nextStep.nextStepPath) {
          navigate(nextStep.path, {state: {nextStepPath: nextStep.nextStepPath}})
        } else {
          navigate(nextStep.path)
        }
        if (!withPaymentStep) {
          setIsLoading(false)
        }
      })
    },
    [type, isLoggedIn, withPaymentStep, bookingState, setValues, submitForm, validateEntityPermission, navigate],
  )

  useEffect(() => {
    void validateEntityPermission()
  }, [validateEntityPermission])

  return (
    <GridLayout header={<Header onBack={handleBack} />}>
      <Box>
        <IntegratedSignUpForm callback={handleBook}>
          <UserInfoSection
            withPaymentStep={withPaymentStep}
            isLoading={isLoading}
            bookingState={bookingState}
          />
        </IntegratedSignUpForm>
      </Box>
      <Box>
        {type === 'class' ? (
          <ClassSummarySection
            setIsLoading={setIsLoading}
            setWholePrice={setWholePrice}
          />
        ) : (
          <ServiceSummarySection
            setIsLoading={setIsLoading}
            setWholePrice={setWholePrice}
            withPromo={!withPaymentStep}
          />
        )}
      </Box>
    </GridLayout>
  )
}
